import { useEffect } from "react";
import type { useVideoEngine, VideoEngineState } from "./useVideoEngine";

type Actions = ReturnType<typeof useVideoEngine>["actions"];

export function usePlayerHotkeys(
  state: VideoEngineState,
  actions: Actions,
  options: { enabled: boolean; onToggleFullscreen: () => void }
) {
  const { enabled, onToggleFullscreen } = options;

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable
      ) {
        return;
      }
      switch (event.code) {
        case "Space":
        case "KeyK":
          event.preventDefault();
          actions.togglePlay();
          break;
        case "ArrowLeft":
        case "KeyJ":
          event.preventDefault();
          actions.seekBy(-5);
          break;
        case "ArrowRight":
        case "KeyL":
          event.preventDefault();
          actions.seekBy(5);
          break;
        case "ArrowUp": {
          event.preventDefault();
          // Treat muted as an effective volume of 0 so Up always unmutes
          // (delta > 0), mirroring reference Shortcut.js behavior.
          const effectiveUp = state.muted ? 0 : state.volume;
          actions.setVolume(Math.min(1, effectiveUp + 0.1));
          break;
        }
        case "ArrowDown": {
          event.preventDefault();
          // Same effective-volume treatment so Down never *un*mutes: from a
          // muted state this clamps to 0, and setVolume(0) leaves muted=true.
          const effectiveDown = state.muted ? 0 : state.volume;
          actions.setVolume(Math.max(0, effectiveDown - 0.1));
          break;
        }
        case "KeyM":
          actions.toggleMute();
          break;
        case "KeyF":
          onToggleFullscreen();
          break;
        default:
          break;
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [enabled, actions, onToggleFullscreen, state.volume, state.muted]);
}
